// C4 — the Cleanup glossary (UX rule #9: "plain words, with help on
// tap"). ONE source of truth for every Cleanup term's plain-language
// meaning, shared by the inline `?` tooltip AND the /cleanup/help
// page. Pure + root-safe (imports nothing) so it is unit-tested in
// isolation and can't drift between the two surfaces.
//
// Voice rule: a non-expert advisor must understand each term without
// any other Cleanup knowledge. No jargon defined with jargon.

export interface GlossaryEntry {
  term: string;
  // One or two plain sentences. First sentence = what it IS; second
  // (optional) = when it matters / what to do.
  definition: string;
}

// Core vocabulary (the terms that carry an inline `?` in the UI).
export const CLEANUP_GLOSSARY: Readonly<Record<string, GlossaryEntry>> = {
  cleanup: {
    term: "Cleanup",
    definition:
      "The one place to fix data problems — duplicates, missing info, records that need a judgment call. It's a permanent part of the system, not just launch work: it keeps watching as new data arrives. Right now there's also a one-time push to clear everything before cutover.",
  },
  issue: {
    term: "Issue",
    definition:
      "One data problem to fix — a possible duplicate, a record missing info, an unmatched payment. Everything in Cleanup is an Issue, handled the same way.",
  },
  lane: {
    term: "Lane",
    definition:
      "A grouped list of similar Issues (e.g. “Possible duplicates”). Pick a lane, then work down the queue.",
  },
  survivor: {
    term: "Survivor",
    definition:
      "When two records are merged, the one that is kept. Its values win and the other record’s links move onto it. Choose the record that holds the most — and the most sensitive (SSN, bank) — data, because those don’t transfer.",
  },
  provenance: {
    term: "Provenance",
    definition:
      "Where a value came from — typed by a person, an import, a merge, or a rollback — and when. Hover any field to see it.",
  },
  tier: {
    term: "Tier",
    definition:
      "How much friction an action needs, set by blast radius: Auto (safe, can batch), Standard (one click), Confirm (type-to-confirm + a reason), Restricted (admin only).",
  },
  reconcile: {
    term: "Reconcile",
    definition:
      "Re-scan the data so fixed Issues disappear and newly-appeared problems show up. Runs automatically on a schedule and after edits; can also be triggered.",
  },
  detector: {
    term: "Detector",
    definition:
      "The rule that finds one kind of Issue (e.g. “same phone and last name, different first name”). Detectors run continuously in the background.",
  },
  confidence: {
    term: "Confidence",
    definition:
      "How sure the detector is that this is a real problem (0–100%). Higher means more likely a true issue worth acting on.",
  },
  risk: {
    term: "Risk",
    definition:
      "How damaging it would be to act wrongly here. Higher risk earns more friction before the action commits.",
  },
  recommendation: {
    term: "Recommended action",
    definition:
      "The one-click action Cleanup suggests. Following it needs no extra typing; choosing a different action is always allowed but asks for a one-line reason.",
  },
  dismiss: {
    term: "Dismiss",
    definition:
      "Mark an Issue “not a problem”. It stays gone unless the underlying data changes materially, then it reopens with what changed.",
  },
  snooze: {
    term: "Snooze",
    definition:
      "Hide an Issue for a while. It comes back later if it’s still unresolved.",
  },
  undo: {
    term: "Undo",
    definition:
      "Reverse the action for 60 seconds. Cleanup is reversible by default; only genuinely destructive actions use type-to-confirm instead.",
  },
  flag: {
    term: "Flag a problem",
    definition:
      "Manually report something off about a record — even one with no detected Issues. It becomes a tracked Issue anyone can pick up. Never a dead-end note.",
  },
  reopened: {
    term: "Reopened",
    definition:
      "An Issue you resolved that came back because the data changed again since. It shows what changed since your decision.",
  },
} as const;

// Friction tiers, spelled out for the help page.
export const TIER_EXPLAINER: ReadonlyArray<{
  tier: string;
  meaning: string;
}> = [
  {
    tier: "Auto",
    meaning:
      "High-confidence, low-blast-radius. Can be applied in bulk; still 60-second undoable.",
  },
  {
    tier: "Standard",
    meaning: "One employee decision, one click. The everyday case.",
  },
  {
    tier: "Confirm",
    meaning:
      "Bigger blast radius (bulk merges, archives, cross-household moves). Type-to-confirm + a reason code.",
  },
  {
    tier: "Restricted",
    meaning:
      "Genuinely destructive or sensitive (permanent delete, SSN/bank overrides). system_admin only, two-step.",
  },
];

// Reason codes: the canonical change_reason_codes enum (CLAUDE.md
// reversibility-first doctrine) with a plain "when to use this" so the
// picker isn't a guessing game. Static — these are doctrine, not data.
export const REASON_CODE_HELP: ReadonlyArray<{
  code: string;
  /** Plain-language label for the picker (UX rule #9 — no jargon). */
  label: string;
  when: string;
}> = [
  {
    code: "duplicate_import_artifact",
    label: "Duplicate from an import",
    when: "The duplicate exists only because an import created a second row for the same person.",
  },
  {
    code: "married_couple_consolidation",
    label: "Consolidating a married couple",
    when: "Two contacts are actually one household (spouses) being brought together.",
  },
  {
    code: "wrong_advisor_assignment",
    label: "Wrong advisor / owner",
    when: "The advisor/owner on the record was incorrect and is being corrected.",
  },
  {
    code: "client_requested_removal",
    label: "Client requested removal",
    when: "The client asked to be removed or merged.",
  },
  {
    code: "deceased",
    label: "Deceased",
    when: "The contact is deceased and the record is being archived/closed.",
  },
  {
    code: "compliance_correction",
    label: "Compliance correction",
    when: "A compliance review requires this change.",
  },
  {
    code: "data_quality_cleanup",
    label: "Data-quality cleanup",
    when: "Routine hygiene — fixing capitalization, formatting, or stale values.",
  },
  {
    code: "other_with_text",
    label: "Other — type a reason",
    when: "None of the above — you must type a short free-text reason.",
  },
];

// Lookup used by the inline tooltip. Case-insensitive on the key;
// returns null for unknown terms so the caller can render plain text.
export function lookupGlossary(key: string): GlossaryEntry | null {
  return CLEANUP_GLOSSARY[key.trim().toLowerCase()] ?? null;
}

// Stable ordering for the /cleanup/help page (definition order of the
// object is insertion order, but expose an explicit list so the page
// and a test agree).
export const GLOSSARY_ORDER: readonly string[] = Object.keys(
  CLEANUP_GLOSSARY,
);
